import { CircularProgress, Grid, Button, FormControl, InputLabel, Select, MenuItem, ButtonGroup } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ResponsiveSankey } from '@nivo/sankey';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { Lightbulb } from '@mui/icons-material';
import dayjs from 'dayjs';
import { getAllFacilities, getAllProject, getWaterBalanceData } from '../../Redux/actions';
import InsightsModal from '../Common/InsightsModal';
import { formatDate, generateInsightKey } from '../../utils';

// Date filter options for digitalisation view
const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today', days: 0 },
    { label: 'Previous Day', value: 'previous', days: 1 },
    { label: '7 - Days', value: '7days', days: 7 },
    { label: '30 - Days', value: '30days', days: 30 },
    { label: '90 - Days', value: '90days', days: 90 },
];

const CustomTooltip = (params) => {
    const { link, node } = params;
    if (link) {
        // Use full original labels from source/target nodes
        const sourceLabel = link.source?.data?.originalLabel || link.source?.label;
        const targetLabel = link.target?.data?.originalLabel || link.target?.label;
        
        // For energy/OCEMS links, don't show value since it's already in the target label
        const isEnergyLink = sourceLabel && (
            sourceLabel.startsWith('STP Energy') || 
            sourceLabel.startsWith('WTP Energy') || 
            sourceLabel.startsWith('ETP Energy')
        );
        const isOcemsLink = sourceLabel && (
            sourceLabel === 'pH' || 
            sourceLabel === 'TSS' || 
            sourceLabel === 'BOD' || 
            sourceLabel === 'COD'
        );
        
        return (<div
            style={{
                padding: '6px 10px',
                background: 'white',
                fontSize: '12px',
                border: '1px solid #ccc',
                minWidth: '200px',
                maxWidth: '400px',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
            }}
        >
            <strong>{sourceLabel} → {targetLabel}{(isEnergyLink || isOcemsLink) ? '' : ` : ${link.value}`}</strong>
        </div>)
    } else {
        // Use full original label if available
        const fullLabel = node?.data?.originalLabel || node?.label;
        
        // Don't show value for energy/OCEMS nodes (they use fixed value for consistent height)
        const isEnergyNode = fullLabel && (
            fullLabel.startsWith('STP Energy') || 
            fullLabel.startsWith('WTP Energy') || 
            fullLabel.startsWith('ETP Energy')
        );
        const isOcemsNode = fullLabel && (
            fullLabel === 'pH' || fullLabel === 'TSS' || 
            fullLabel === 'BOD' || fullLabel === 'COD' ||
            fullLabel.startsWith('pH -') || fullLabel.startsWith('TSS -') || 
            fullLabel.startsWith('BOD -') || fullLabel.startsWith('COD -')
        );
        
        let valueLabel = '';
        if (!isEnergyNode && !isOcemsNode && node.label.indexOf('(') !== -1) {
            valueLabel = `(${node.value})`;
        }
        
        return <div
            style={{
                padding: '6px 10px',
                background: 'white',
                fontSize: '12px',
                border: '1px solid #ccc',
                maxWidth: '400px',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
            }}
        >
            <strong>{fullLabel} {valueLabel}</strong>
        </div>
    }
}

// Legend component for water and energy categories
const ChartLegend = () => {
    const waterLegendItems = [
        { label: 'Fresh Water', color: 'green' },
        { label: 'Recycled Water', color: '#ffa600' },
        { label: 'Effluent Water (ETP)', color: '#e377c2' },
        { label: 'RO System', color: '#17becf' },
        { label: 'Unmetered/Wastage', color: '#ff0000' }
    ];

    const energyLegendItems = [
        { label: 'STP Energy', color: '#8B5CF6' },
        { label: 'WTP Energy', color: '#4F46E5' },
        { label: 'ETP Energy', color: '#9b24d6' }
    ];

    const ocemsLegendItems = [
        { label: 'pH', color: '#2196F3' },
        { label: 'TSS', color: '#FF9800' },
        { label: 'BOD', color: '#4CAF50' },
        { label: 'COD', color: '#9C27B0' }
    ];

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: 'white',
            borderRadius: '6px',
            border: '1px solid #e0e0e0',
            fontSize: '12px',
            marginLeft: '16px',
            width: 'fit-content'
        }}>
            {/* Water Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>Water:</span>
                {waterLegendItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: item.color,
                            borderRadius: '2px'
                        }} />
                        <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                    </div>
                ))}
            </div>
            {/* Energy Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>Energy:</span>
                {energyLegendItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: item.color,
                            borderRadius: '2px'
                        }} />
                        <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                    </div>
                ))}
            </div>
            {/* OCEMS (RTM) Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>OCEMS (RTM):</span>
                {ocemsLegendItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: item.color,
                            borderRadius: '2px'
                        }} />
                        <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function WaterBalanceSankey({ clientId, projectIds = null, showFilters = true }) {
    const dispatch = useDispatch();
    const [sankeyData, setSankeyData] = useState(null);
    const [sankeyLoader, setSankeyLoader] = useState(false);
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
    const maxDate = dayjs().subtract(1, 'days').toDate();
    const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'days').toDate());
    const [toDate, setToDate] = useState(dayjs().subtract(1, 'days').toDate());
    const [selectedDateFilter, setSelectedDateFilter] = useState('30days'); // Default to 30 days for digitalisation view

    const [openInsightModal, setOpenInsightModal] = useState(false);
    const [showInsightButton, setShowInsightButton] = useState(true);
    const [insightKey, setInsightKey] = useState(null);

    // Create stable reference for projectIds to prevent infinite loop
    const projectIdsKey = useMemo(() => {
        return projectIds ? JSON.stringify(projectIds) : null;
    }, [projectIds]);

    // Split sankeyData into separate category data (fresh_water, recycled_water, etp, ro, energy, ocems)
    const { freshWaterData, recycledWaterData, etpData, roData, energySankeyData, levelData, ocemsData } = useMemo(() => {
        if (!sankeyData || !sankeyData.nodes || !sankeyData.links) {
            return { 
                freshWaterData: null, 
                recycledWaterData: null, 
                etpData: null, 
                roData: null, 
                energySankeyData: null,
                levelData: null,
                ocemsData: null
            };
        }

        // Helper function to extract data for a specific category
        const extractCategoryData = (category) => {
            const categoryNodes = sankeyData.nodes.filter(node => node.category === category);
            if (categoryNodes.length === 0) return null;

            const categoryNodeIds = new Set(categoryNodes.map(node => node.id));
            const categoryLinks = sankeyData.links.filter(link => 
                categoryNodeIds.has(link.source) || categoryNodeIds.has(link.target)
            );

            // Include any connected nodes that may have different categories (like unmetered nodes)
            categoryLinks.forEach(link => {
                if (!categoryNodeIds.has(link.source)) {
                    const sourceNode = sankeyData.nodes.find(n => n.id === link.source);
                    if (sourceNode && !categoryNodes.find(n => n.id === sourceNode.id)) {
                        categoryNodes.push(sourceNode);
                        categoryNodeIds.add(sourceNode.id);
                    }
                }
                if (!categoryNodeIds.has(link.target)) {
                    const targetNode = sankeyData.nodes.find(n => n.id === link.target);
                    if (targetNode && !categoryNodes.find(n => n.id === targetNode.id)) {
                        categoryNodes.push(targetNode);
                        categoryNodeIds.add(targetNode.id);
                    }
                }
            });

            return categoryNodes.length > 0 ? { nodes: categoryNodes, links: categoryLinks } : null;
        };

        return {
            freshWaterData: extractCategoryData('fresh_water'),
            recycledWaterData: extractCategoryData('recycled_water'),
            etpData: extractCategoryData('etp'),
            roData: extractCategoryData('ro'),
            energySankeyData: extractCategoryData('energy'),
            levelData: extractCategoryData('level'),
            ocemsData: extractCategoryData('ocems')
        };
    }, [sankeyData]);

    // Helper function to get date range based on selected filter
    const getDateRangeFromFilter = (filterValue) => {
        const today = dayjs();
        let from, to;
        
        switch (filterValue) {
            case 'today':
                from = today.startOf('day');
                to = today;
                break;
            case 'previous':
                from = today.subtract(1, 'day').startOf('day');
                to = today.subtract(1, 'day').endOf('day');
                break;
            case '7days':
                from = today.subtract(7, 'days');
                to = today.subtract(1, 'day');
                break;
            case '30days':
                from = today.subtract(30, 'days');
                to = today.subtract(1, 'day');
                break;
            case '90days':
                from = today.subtract(90, 'days');
                to = today.subtract(1, 'day');
                break;
            default:
                from = today.subtract(30, 'days');
                to = today.subtract(1, 'day');
        }
        
        return { from: from.toDate(), to: to.toDate() };
    };

    // Handle date filter button click (for digitalisation view)
    const handleDateFilterChange = (filterValue) => {
        setSelectedDateFilter(filterValue);
        const { from, to } = getDateRangeFromFilter(filterValue);
        setFromDate(from);
        setToDate(to);

        // Fetch data with new date range
        if (projectIds && projectIds.length > 0) {
            setSankeyLoader(true);
            const params = {
                project_ids: projectIds,
                from_date: dayjs(from).format('YYYY-MM-DD') + ' 00:00:00',
                to_date: dayjs(to).format('YYYY-MM-DD') + ' 23:59:59'
            };

            dispatch(getWaterBalanceData(params)).then((res) => {
                setSankeyLoader(false);
                if (res && res.status === 200 && res.data?.success) {
                    setSankeyData(res.data.data?.chart_data);
                }
            }).catch(() => {
                setSankeyLoader(false);
            });
        }
    };

    // Get display label for current filter
    const getFilterDisplayLabel = () => {
        const filter = DATE_FILTER_OPTIONS.find(f => f.value === selectedDateFilter);
        return filter ? filter.label : '30 - Days';
    };

    // Prepare insight modal with sankey data
    const prepareInsightModal = () => {
        const formattedFromDate = formatDate(fromDate);
        const formattedToDate = formatDate(toDate);
        // Include projectIds in the key to make it unique for different project combinations
        const projectIdsString = projectIds ? projectIds.sort().join('-') : (selectedProjects.length > 0 ? selectedProjects.sort().join('-') : 'all');
        const key = generateInsightKey(clientId, formattedFromDate, formattedToDate, `water-balance-sankey-${projectIdsString}`);
        setInsightKey(key);
        setOpenInsightModal(true);
        setShowInsightButton(false);
    };

    useEffect(() => {
        if (!clientId) return;

        // If projectIds are provided (Live View mode), fetch data directly without filters
        if (projectIds && projectIds.length > 0) {
            setHasAppliedFilters(true);
            fetchSankeyDataWithParams([], projectIds);
            return;
        }

        // Otherwise, fetch facilities and projects for filters (Dashboard mode)
        if (showFilters) {
            Promise.all([
                dispatch(getAllFacilities(clientId)),
                dispatch(getAllProject(clientId))
            ]).then(([facilitiesRes, projectsRes]) => {
                const facilityList = facilitiesRes?.data?.allData || [];
                const projectList = projectsRes?.data?.allData || [];

                setFacilities(facilityList);
                setProjects(projectList);

                // Auto-select all facilities and projects
                if (facilityList.length > 0 && projectList.length > 0) {
                    const facilityIds = facilityList.map(f => f.id);
                    const projectIdsAll = projectList.map(p => p.pid);

                    setSelectedFacilities(facilityIds);
                    setSelectedProjects(projectIdsAll);
                    setHasAppliedFilters(true);

                    // Trigger fetchSankeyData after both are loaded
                    setTimeout(() => {
                        fetchSankeyDataWithParams(facilityIds, projectIdsAll);
                    }, 100);
                }
            });
        }
    }, [clientId, projectIdsKey, showFilters, dispatch])

    // Filter projects based on selected facilities
    useEffect(() => {
        if (selectedFacilities.length > 0 && projects.length > 0) {
            const filtered = projects.filter(project =>
                selectedFacilities.includes(project.facility_id)
            );
            setFilteredProjects(filtered);
            // Auto-select all filtered projects
            if (filtered.length > 0) {
                const filteredProjectIds = filtered.map(p => p.pid);
                setSelectedProjects(filteredProjectIds);
            } else {
                setSelectedProjects([]);
            }
        } else {
            setFilteredProjects([]);
            setSelectedProjects([]);
        }
    }, [selectedFacilities, projects])

    const fetchSankeyDataWithParams = (facilityIds, projectIds) => {
        setSankeyLoader(true);
        const params = {};
        if (facilityIds && facilityIds.length > 0) params.facility_ids = facilityIds;
        if (projectIds && projectIds.length > 0) params.project_ids = projectIds;
        if (fromDate) params.from_date = dayjs(fromDate).format('YYYY-MM-DD') + ' 00:00:00';
        if (toDate) params.to_date = dayjs(toDate).format('YYYY-MM-DD') + ' 00:00:00';

        dispatch(getWaterBalanceData(params)).then((res) => {
            setSankeyLoader(false);
            if (res && res.status === 200 && res.data?.success) {
                setSankeyData(res.data.data?.chart_data);
            } else {
                setSankeyData(res.data.message);
            }
        }).catch(() => {
            setSankeyLoader(false);
        });
    };

    const fetchSankeyData = () => {
        fetchSankeyDataWithParams(selectedFacilities, selectedProjects);
    };

    const handleApplyFilters = () => {
        setHasAppliedFilters(true);
        fetchSankeyData();
    };

    const handleClearFilters = () => {
        setSelectedFacilities([]);
        setSelectedProjects([]);
        const newFromDate = dayjs().subtract(30, 'days').toDate();
        const newToDate = dayjs().subtract(1, 'days').toDate();
        setFromDate(newFromDate);
        setToDate(newToDate);
        setSankeyData(null);
        setHasAppliedFilters(false);
    };

    const handleFilterChange = (event, filterType) => {
        const { target: { value } } = event;

        const stateMapping = {
            facility: {
                allItems: facilities?.map(facility => facility.id),
                selectedItems: selectedFacilities,
                setSelected: setSelectedFacilities,
                allLabel: "Select All"
            },
            project: {
                allItems: filteredProjects?.map(project => project.pid),
                selectedItems: selectedProjects,
                setSelected: setSelectedProjects,
                allLabel: "Select All"
            }
        };

        const { allItems, selectedItems, setSelected, allLabel } = stateMapping[filterType];

        if (value.includes(allLabel)) {
            if (selectedItems.length === allItems.length) {
                setSelected([]);
            } else {
                setSelected(allItems);
            }
        } else {
            const newValue = typeof value === 'string' ? value.split(',') : value;
            const filteredValue = newValue.filter(item => item !== allLabel);
            setSelected(filteredValue);
        }
    };

    // Wrapper style for Live view to remove side padding
    const liveViewWrapperStyle = !showFilters ? {
        marginLeft: '-24px',
        width: 'calc(100% + 48px)'
    } : {};

    return (
        <div style={liveViewWrapperStyle}>
            {!showFilters && (
                <Grid container spacing={2} style={{ marginBottom: 16, marginTop: 24 }}>
                    <Grid item xs={12}>
                        <h5 style={{ color: '#004d4d' }} className="card-title">Water, Energy & OCEMS Mapping</h5>
                        <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                            Showing data for {getFilterDisplayLabel()}
                        </p>
                    </Grid>
                </Grid>
            )}
            {showFilters && (
                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 24, marginTop: 24 }}>
                    <Grid item xs={12}>
                        <h5 style={{ color: '#004d4d' }} className="card-title">Water, Energy & OCEMS Mapping</h5>
                        <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                            Showing: {selectedFacilities.length === facilities.length || selectedFacilities.length === 0 ? 'All Facilities' : `${selectedFacilities.length} Facilities`}
                            {' | '}
                            {selectedProjects.length === filteredProjects.length || selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} Projects`}
                        </p>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <FormControl size='small' fullWidth>
                            <InputLabel id="facility-select-label" sx={{ '&.Mui-focused': { color: '#008080' } }}>Facility*</InputLabel>
                            <Select
                                labelId="facility-select-label"
                                multiple
                                value={selectedFacilities}
                                onChange={(e) => handleFilterChange(e, 'facility')}
                                input={<OutlinedInput label="Facility" />}
                                renderValue={(selected) => selected.length === facilities?.length ? 'All Facilities' : selected.length + ' selected'}
                                sx={{
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#008080'
                                    }
                                }}
                                MenuProps={{
                                    autoFocus: false,
                                    disableScrollLock: true,
                                    PaperProps: {
                                        style: {
                                            maxHeight: 350,
                                            overflowY: "auto"
                                        }
                                    }
                                }}
                            >
                                {facilities?.length > 0 && (
                                    <MenuItem value="Select All">
                                        <Checkbox
                                            checked={selectedFacilities?.length === facilities?.length}
                                            indeterminate={selectedFacilities?.length > 0 && selectedFacilities?.length < facilities?.length}
                                            sx={{ '&.Mui-checked': { color: '#008080' }, '&.MuiCheckbox-indeterminate': { color: '#008080' } }}
                                        />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                )}
                                {facilities.map((facility) => (
                                    <MenuItem key={facility.id} value={facility.id}>
                                        <Checkbox checked={selectedFacilities?.includes(facility.id)} sx={{ '&.Mui-checked': { color: '#008080' } }} />
                                        <ListItemText primary={facility.facility_name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <FormControl size='small' fullWidth>
                            <InputLabel id="project-select-label" sx={{ '&.Mui-focused': { color: '#008080' } }}>Project*</InputLabel>
                            <Select
                                labelId="project-select-label"
                                multiple
                                disabled={selectedFacilities?.length === 0}
                                value={selectedProjects}
                                onChange={(e) => handleFilterChange(e, 'project')}
                                input={<OutlinedInput label="Project" />}
                                renderValue={(selected) => selected.length === filteredProjects?.length ? 'All Projects' : selected.length + ' selected'}
                                sx={{
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#008080'
                                    }
                                }}
                                MenuProps={{
                                    autoFocus: false,
                                    disableScrollLock: true,
                                    PaperProps: {
                                        style: {
                                            maxHeight: 350,
                                            overflowY: "auto"
                                        }
                                    }
                                }}
                            >
                                {filteredProjects?.length > 0 && (
                                    <MenuItem value="Select All">
                                        <Checkbox
                                            checked={selectedProjects?.length === filteredProjects?.length}
                                            indeterminate={selectedProjects?.length > 0 && selectedProjects?.length < filteredProjects?.length}
                                            sx={{ '&.Mui-checked': { color: '#008080' }, '&.MuiCheckbox-indeterminate': { color: '#008080' } }}
                                        />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                )}
                                {filteredProjects.map((project) => (
                                    <MenuItem key={project.pid} value={project.pid}>
                                        <Checkbox checked={selectedProjects?.includes(project.pid)} sx={{ '&.Mui-checked': { color: '#008080' } }} />
                                        <ListItemText primary={project.projname.replace("WA-", "").trim()} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                            <DatePicker
                                label="From Date"
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: {
                                            '& .MuiInputBase-input': {
                                                minWidth: 0 + ' !important'
                                            },
                                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#008080'
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#008080'
                                            }
                                        }
                                    },
                                    day: {
                                        sx: {
                                            '&.Mui-selected': {
                                                backgroundColor: '#008080 !important',
                                                '&:hover': {
                                                    backgroundColor: '#006666 !important'
                                                }
                                            }
                                        }
                                    }
                                }}
                                value={dayjs(fromDate)}
                                maxDate={dayjs(maxDate)}
                                onChange={(newValue) => setFromDate(dayjs(newValue).toDate())}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                            <DatePicker
                                label="To Date"
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: {
                                            '& .MuiInputBase-input': {
                                                minWidth: 0 + ' !important'
                                            },
                                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#008080'
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#008080'
                                            }
                                        }
                                    },
                                    day: {
                                        sx: {
                                            '&.Mui-selected': {
                                                backgroundColor: '#008080 !important',
                                                '&:hover': {
                                                    backgroundColor: '#006666 !important'
                                                }
                                            }
                                        }
                                    }
                                }}
                                value={dayjs(toDate)}
                                maxDate={dayjs(maxDate)}
                                minDate={dayjs(fromDate)}
                                onChange={(newValue) => setToDate(dayjs(newValue).toDate())}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleApplyFilters}
                            style={{ height: '40px', backgroundColor: '#008080' }}
                        >
                            Apply
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleClearFilters}
                            style={{ height: '40px', color: '#008080', borderColor: '#008080' }}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={2} style={{ marginBottom: '24px' }}>
                <Grid item xs={12}>
                    {sankeyLoader ? (
                        <div className='w3-center' style={{ marginTop: 30 }}>
                            <CircularProgress />
                        </div>
                    ) : !hasAppliedFilters ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            color: '#666',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '8px',
                            border: '1px dashed #ddd'
                        }}>
                            <p style={{ fontSize: '16px', margin: 0 }}>Select filters and click Apply</p>
                        </div>
                    ) : sankeyData && sankeyData.nodes && sankeyData.nodes.length > 0 ? (
                        <div style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 16px 0px 0px', backgroundColor: '#fafafa' }}>
                            {showFilters && (
                                <div style={{ marginBottom: '12px', marginLeft: '0px' }}>
                                    <ChartLegend />
                                </div>
                            )}
                            {!showFilters && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <div>
                                            <ChartLegend />
                                        </div>
                                        <div>
                                            {DATE_FILTER_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    style={{
                                                        backgroundColor: selectedDateFilter === option.value ? '#008080' : '#6c757d',
                                                        color: '#fff',
                                                        marginRight: 4
                                                    }}
                                                    className="btn btn-secondary btn-sm tabs-nprevious-days"
                                                    onClick={() => handleDateFilterChange(option.value)}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {sankeyData && showInsightButton && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                style={{ borderColor: '#008080', color: '#008080' }}
                                                id="water-balance-sankey-insight"
                                                size='small'
                                                startIcon={<Lightbulb style={{ color: '#008080' }} />}
                                                variant='outlined'
                                                onClick={prepareInsightModal}
                                            >
                                                View Insights
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Fresh Water Sankey Chart */}
                            {freshWaterData && freshWaterData.nodes.length > 0 && (
                                <div style={{ marginBottom: '8px'}}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 'bold', 
                                        color: '#006400', 
                                        marginBottom: '4px',
                                        marginLeft: '220px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div style={{ width: '14px', height: '14px', backgroundColor: 'green', borderRadius: '2px' }} />
                                        Fresh Water
                                    </div>
                                    <div style={{ height: '250px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                        <ResponsiveSankey
                                            data={freshWaterData}
                                            margin={{ top: 20, right: 220, bottom: 10, left: 220 }}
                                            nodeOpacity={1}
                                            nodeThickness={15}
                                            colors={{ datum: "color" }}
                                            nodeSpacing={40}
                                            nodeBorderWidth={0}
                                            linkTooltip={CustomTooltip}
                                            nodeTooltip={CustomTooltip}
                                            nodeBorderRadius={4}
                                            enableLinkGradient={true}
                                            labelOrientation="horizontal"
                                            labelPosition='outside'
                                            labelPadding={10}
                                            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                            label={(node) => {
                                                const originalLabel = node.label || node.id || '';
                                                if (!node.data) {
                                                    node.data = {};
                                                }
                                                node.data.originalLabel = originalLabel;
                                                const maxLength = 35;
                                                if (originalLabel && originalLabel.length > maxLength) {
                                                    return originalLabel.substring(0, maxLength) + '...';
                                                }
                                                return originalLabel;
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Recycled Water Sankey Chart */}
                            {recycledWaterData && recycledWaterData.nodes.length > 0 && (
                                <div style={{ 
                                    marginTop: freshWaterData ? '16px' : '0', 
                                    borderTop: freshWaterData ? '1px dashed #ccc' : 'none', 
                                    paddingTop: freshWaterData ? '8px' : '0',
                                }}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 'bold', 
                                        color: '#cc8400', 
                                        marginBottom: '4px',
                                        marginLeft: '220px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div style={{ width: '14px', height: '14px', backgroundColor: '#ffa600', borderRadius: '2px' }} />
                                        Recycled Water
                                    </div>
                                    <div style={{ height: '250px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                        <ResponsiveSankey
                                            data={recycledWaterData}
                                            margin={{ top: 20, right: 220, bottom: 10, left: 220 }}
                                            nodeOpacity={1}
                                            nodeThickness={15}
                                            colors={{ datum: "color" }}
                                            nodeSpacing={40}
                                            nodeBorderWidth={0}
                                            linkTooltip={CustomTooltip}
                                            nodeTooltip={CustomTooltip}
                                            nodeBorderRadius={4}
                                            enableLinkGradient={true}
                                            labelOrientation="horizontal"
                                            labelPosition='outside'
                                            labelPadding={10}
                                            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                            label={(node) => {
                                                const originalLabel = node.label || node.id || '';
                                                if (!node.data) {
                                                    node.data = {};
                                                }
                                                node.data.originalLabel = originalLabel;
                                                const maxLength = 35;
                                                if (originalLabel && originalLabel.length > maxLength) {
                                                    return originalLabel.substring(0, maxLength) + '...';
                                                }
                                                return originalLabel;
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ETP Sankey Chart */}
                            {etpData && etpData.nodes.length > 0 && (
                                <div style={{ 
                                    marginTop: (freshWaterData || recycledWaterData) ? '16px' : '0', 
                                    borderTop: (freshWaterData || recycledWaterData) ? '1px dashed #ccc' : 'none', 
                                    paddingTop: (freshWaterData || recycledWaterData) ? '8px' : '0'
                                }}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 'bold', 
                                        color: '#b35a9d', 
                                        marginBottom: '4px',
                                        marginLeft: '220px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div style={{ width: '14px', height: '14px', backgroundColor: '#e377c2', borderRadius: '2px' }} />
                                        Effluent Treatment (ETP)
                                    </div>
                                    <div style={{ height: '250px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                        <ResponsiveSankey
                                            data={etpData}
                                            margin={{ top: 20, right: 220, bottom: 10, left: 220 }}
                                            nodeOpacity={1}
                                            nodeThickness={15}
                                            colors={{ datum: "color" }}
                                            nodeSpacing={40}
                                            nodeBorderWidth={0}
                                            linkTooltip={CustomTooltip}
                                            nodeTooltip={CustomTooltip}
                                            nodeBorderRadius={4}
                                            enableLinkGradient={true}
                                            labelOrientation="horizontal"
                                            labelPosition='outside'
                                            labelPadding={10}
                                            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                            label={(node) => {
                                                const originalLabel = node.label || node.id || '';
                                                if (!node.data) {
                                                    node.data = {};
                                                }
                                                node.data.originalLabel = originalLabel;
                                                const maxLength = 35;
                                                if (originalLabel && originalLabel.length > maxLength) {
                                                    return originalLabel.substring(0, maxLength) + '...';
                                                }
                                                return originalLabel;
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* RO System Sankey Chart */}
                            {roData && roData.nodes.length > 0 && (
                                <div style={{ 
                                    marginTop: (freshWaterData || recycledWaterData || etpData) ? '16px' : '0', 
                                    borderTop: (freshWaterData || recycledWaterData || etpData) ? '1px dashed #ccc' : 'none', 
                                    paddingTop: (freshWaterData || recycledWaterData || etpData) ? '8px' : '0'
                                }}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 'bold', 
                                        color: '#0d8a99', 
                                        marginBottom: '4px',
                                        marginLeft: '220px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div style={{ width: '14px', height: '14px', backgroundColor: '#17becf', borderRadius: '2px' }} />
                                        RO System
                                    </div>
                                    <div style={{ height: '250px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                        <ResponsiveSankey
                                            data={roData}
                                            margin={{ top: 20, right: 220, bottom: 10, left: 220 }}
                                            nodeOpacity={1}
                                            nodeThickness={15}
                                            colors={{ datum: "color" }}
                                            nodeSpacing={40}
                                            nodeBorderWidth={0}
                                            linkTooltip={CustomTooltip}
                                            nodeTooltip={CustomTooltip}
                                            nodeBorderRadius={4}
                                            enableLinkGradient={true}
                                            labelOrientation="horizontal"
                                            labelPosition='outside'
                                            labelPadding={10}
                                            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                            label={(node) => {
                                                const originalLabel = node.label || node.id || '';
                                                if (!node.data) {
                                                    node.data = {};
                                                }
                                                node.data.originalLabel = originalLabel;
                                                const maxLength = 35;
                                                if (originalLabel && originalLabel.length > maxLength) {
                                                    return originalLabel.substring(0, maxLength) + '...';
                                                }
                                                return originalLabel;
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Energy Consumption Sankey Chart - Separate section with fixed small height */}
                            {energySankeyData && energySankeyData.nodes.length > 0 && (
                                <div style={{ 
                                    marginTop: '16px', 
                                    borderTop: '1px dashed #ccc', 
                                    paddingTop: '8px'
                                }}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 'bold', 
                                        color: '#666', 
                                        marginBottom: '4px',
                                        marginLeft: '220px'
                                    }}>
                                        Energy Consumption
                                    </div>
                                    <div style={{ height: '150px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                        <ResponsiveSankey
                                            data={energySankeyData}
                                            margin={{ top: 10, right: 220, bottom: 10, left: 220 }}
                                            nodeOpacity={1}
                                            nodeThickness={12}
                                            colors={{ datum: "color" }}
                                            nodeSpacing={20}
                                            nodeBorderWidth={0}
                                            linkTooltip={CustomTooltip}
                                            nodeTooltip={CustomTooltip}
                                            nodeBorderRadius={4}
                                            enableLinkGradient={true}
                                            labelOrientation="horizontal"
                                            labelPosition='outside'
                                            labelPadding={10}
                                            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                            label={(node) => {
                                                const originalLabel = node.label || node.id || '';
                                                if (!node.data) {
                                                    node.data = {};
                                                }
                                                node.data.originalLabel = originalLabel;
                                                const maxLength = 45;
                                                if (originalLabel && originalLabel.length > maxLength) {
                                                    return originalLabel.substring(0, maxLength) + '...';
                                                }
                                                return originalLabel;
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* OCEMS (RTM) Sankey Chart - Separate section for pH, TSS, BOD, COD */}
                            {ocemsData && ocemsData.nodes.length > 0 && (
                                <div style={{ 
                                    marginTop: '16px', 
                                    borderTop: '1px dashed #ccc', 
                                    paddingTop: '8px'
                                }}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 'bold', 
                                        color: '#666', 
                                        marginBottom: '4px',
                                        marginLeft: '220px'
                                    }}>
                                        OCEMS (RTM)
                                    </div>
                                    <div style={{ height: '150px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                        <ResponsiveSankey
                                            data={ocemsData}
                                            margin={{ top: 10, right: 220, bottom: 10, left: 220 }}
                                            nodeOpacity={1}
                                            nodeThickness={12}
                                            colors={{ datum: "color" }}
                                            nodeSpacing={20}
                                            nodeBorderWidth={0}
                                            linkTooltip={CustomTooltip}
                                            nodeTooltip={CustomTooltip}
                                            nodeBorderRadius={4}
                                            enableLinkGradient={true}
                                            labelOrientation="horizontal"
                                            labelPosition='outside'
                                            labelPadding={10}
                                            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                            label={(node) => {
                                                const originalLabel = node.label || node.id || '';
                                                if (!node.data) {
                                                    node.data = {};
                                                }
                                                node.data.originalLabel = originalLabel;
                                                const maxLength = 45;
                                                if (originalLabel && originalLabel.length > maxLength) {
                                                    return originalLabel.substring(0, maxLength) + '...';
                                                }
                                                return originalLabel;
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 16px 0px 0px', backgroundColor: '#fafafa' }}>
                            {!showFilters && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                    {DATE_FILTER_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            style={{
                                                backgroundColor: selectedDateFilter === option.value ? '#008080' : '#6c757d',
                                                color: '#fff',
                                                marginRight: 4
                                            }}
                                            className="btn btn-secondary btn-sm tabs-nprevious-days"
                                            onClick={() => handleDateFilterChange(option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div style={{
                                textAlign: 'center',
                                padding: '60px 40px',
                                color: '#666',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '8px',
                                border: '1px dashed #ddd'
                            }}>
                                <p style={{ fontSize: '16px', margin: 0 }}>Chart data not available</p>
                            </div>
                        </div>
                    )}
                </Grid>
            </Grid>

            {/* Insights Modal */}
            <InsightsModal
                convertedJson={sankeyData}
                openInsight={openInsightModal}
                closeInsight={() => setOpenInsightModal(false)}
                setShowInsightButton={() => setShowInsightButton(true)}
                insightKey={insightKey}
            />
        </div>
    )
}
